import { useMemo, useState } from 'react';
import { Button, InputSelect } from '@features/ui';
import { ExportIcon } from '@features/ui/icons';
import { ReportProfitableTradersTable } from '@features/admin/reports';
import {
  TReportProfitableTradersV2Package,
  TReportProfitableTradersV2Params,
  TReportProfitableTradersV2Res,
  TOption,
} from '@src/types';
import {
  exportPAProfitableTradersV2,
  getPAProfitableTradersV2,
} from '@src/services/reports.ts';
import { downloadFile } from '@lib/utils.ts';

const PROFIT_OPTIONS: TOption[] = [
  { label: '1000', value: '1000' },
  { label: '1500', value: '1500' },
  { label: '2000', value: '2000' },
  { label: '2500', value: '2500' },
  { label: '3000', value: '3000' },
  { label: '4000', value: '4000' },
];

const PACKAGE_OPTIONS: TOption[] = [
  { label: 'All', value: 'all' },
  { label: 'Super', value: 'super' },
  { label: 'Std', value: 'std' },
];

export const ProfitableTraderV2 = () => {
  const [selectedProfit, setSelectedProfit] = useState('1000');
  const [selectedPackage, setSelectedPackage] =
    useState<TReportProfitableTradersV2Package>('all');
  const [data, setData] = useState<TReportProfitableTradersV2Res | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const payload = useMemo(
    (): TReportProfitableTradersV2Params => ({
      profit_required: Number(selectedProfit),
      package: selectedPackage,
    }),
    [selectedPackage, selectedProfit]
  );

  const handleGetData = async () => {
    try {
      setIsLoading(true);
      setIsInitialized(true);
      const result = await getPAProfitableTradersV2(payload);

      setData(result);
    } catch (error) {
      console.error('Error fetching profitable traders v2 report:', error);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const response = await exportPAProfitableTradersV2(payload);

      downloadFile(response.data, 'pa-profitable-traders-v2');
    } catch (error) {
      console.error('Error exporting profitable traders v2 report:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div>
      <div
        className={
          'flex justify-between lg:items-center gap-7.5 lg:flex-row flex-col'
        }
      >
        <div className={'text-xs flex lg:flex-row flex-col gap-x-8 gap-y-3'}>
          <h1 className={'text-[23px]'}>{`PA Traders in Profit Report (V2)`}</h1>
          <p className={'max-w-[419px]'}>
            {isInitialized
              ? `Found ${data?.total_accounts ?? 0} active PA accounts with profit more than $${selectedProfit}`
              : 'Select filters and click GET DATA'}
          </p>
        </div>
        <Button
          className={'h-9.5 border-1 border-[#C6C6C6] rounded-[3px] w-fit'}
          disabled={isExporting || !isInitialized || isLoading}
          variant={'outline'}
          onClick={handleExport}
        >
          <p>{isExporting ? 'Exporting...' : 'Export'}</p>
          <ExportIcon />
        </Button>
      </div>

      <div className={'flex flex-wrap items-end gap-3 mt-4'}>
        <div className={'min-w-[180px]'}>
          <p className={'text-xs mb-1'}>Profit Required</p>
          <InputSelect
            options={PROFIT_OPTIONS}
            value={selectedProfit}
            onChange={setSelectedProfit}
          />
        </div>

        <div className={'min-w-[180px]'}>
          <p className={'text-xs mb-1'}>Packages</p>
          <InputSelect
            options={PACKAGE_OPTIONS}
            value={selectedPackage}
            onChange={(value) =>
              setSelectedPackage(value as TReportProfitableTradersV2Package)
            }
          />
        </div>

        <Button
          className={'h-9.5 border-1 border-[#C6C6C6] rounded-[3px]'}
          disabled={isLoading}
          variant={'outline'}
          onClick={handleGetData}
        >
          {isLoading ? 'Loading...' : 'GET DATA'}
        </Button>
      </div>

      {isInitialized && <ReportProfitableTradersTable data={data?.data ?? []} />}
    </div>
  );
};
